import { useEffect, useRef, useState } from 'react'
import { aiApi, type AiConfig, type AiJob } from '../api/ai'

interface AiAssistantPanelProps {
  submissionId: string
  onApply?: (applied: Record<string, string | string[]>) => void
}

export function AiAssistantPanel({ submissionId, onApply }: AiAssistantPanelProps) {
  const [pmcId, setPmcId] = useState('')
  const [manuscript, setManuscript] = useState('')
  const [inputMode, setInputMode] = useState<'pmc_id' | 'text'>('pmc_id')
  const [pmcPreview, setPmcPreview] = useState<{ title: string; text: string } | null>(null)
  const [fetchingPmc, setFetchingPmc] = useState(false)
  const [job, setJob] = useState<AiJob | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [config, setConfig] = useState<AiConfig | null>(null)
  const pollRef = useRef<number | null>(null)

  useEffect(() => {
    aiApi.getConfig().then(setConfig).catch(() => {})
  }, [])

  const stopPolling = () => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  const startPolling = (id: string) => {
    stopPolling()
    pollRef.current = window.setInterval(async () => {
      try {
        const j = await aiApi.getJob(id)
        setJob(j)
        if (j.status === 'completed' || j.status === 'failed') {
          stopPolling()
          setLoading(false)
          if (j.status === 'completed') {
            await autoApply(j)
          }
        }
      } catch {
        // Keep polling on transient errors.
      }
    }, 2000)
  }

  useEffect(() => {
    return () => stopPolling()
  }, [])

  const autoApply = async (j: AiJob) => {
    setError(null)
    setMessage(null)
    if (j.results.length === 0) {
      setMessage('No fields were extracted. Try a different article or more detailed text.')
      return
    }
    const accepted = j.results.map((r) => r.question_name)
    try {
      await aiApi.apply({
        submission_id: submissionId,
        job_id: j.id,
        accepted,
      })
      const applied: Record<string, string | string[]> = {}
      for (const r of j.results) {
        applied[r.question_name] = r.value
      }
      setMessage(`Applied ${accepted.length} field(s) to the checklist.`)
      onApply?.(applied)
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Apply failed.')
    }
  }

  const handleFetchPmc = async () => {
    setError(null)
    setMessage(null)
    setPmcPreview(null)

    const raw = pmcId.trim()
    if (!raw) {
      setError('Please enter a PMC ID.')
      return
    }

    setFetchingPmc(true)
    try {
      const res = await aiApi.fetchPmc({ pmc_id: raw })
      if (!res.success || !res.available) {
        setError(res.error || 'Full text is not available for this PMC ID.')
      } else {
        setPmcPreview({ title: res.title, text: res.text })
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Could not fetch PMC article.')
    } finally {
      setFetchingPmc(false)
    }
  }

  const handleExtract = async () => {
    setError(null)
    setMessage(null)
    setJob(null)

    let inputRef: string
    if (inputMode === 'pmc_id') {
      if (!pmcPreview) {
        setError('Please fetch the full text first and make sure it is available.')
        return
      }
      inputRef = pmcPreview.text
    } else {
      inputRef = manuscript.trim()
      if (inputRef.length < 50) {
        setError('Please paste at least 50 characters of manuscript text.')
        return
      }
    }

    setLoading(true)
    try {
      const res = await aiApi.extract({
        submission_id: submissionId,
        input_type: 'text',
        input_ref: inputRef,
      })
      startPolling(res.job_id)
    } catch (err: any) {
      setLoading(false)
      setError(err?.response?.data?.detail || err.message || 'Extraction request failed.')
    }
  }

  const modelName = config
    ? config.default_provider === 'gemini'
      ? config.gemini_model
      : config.deepseek_model
    : ''

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <h2 className="mb-2 text-lg font-semibold text-gray-800">AI Assistant</h2>

      {config && (
        <div className="mb-4 rounded-md bg-blue-50 p-3 text-sm text-blue-800">
          <p className="font-medium">
            LLM in use: <span className="capitalize">{config.default_provider}</span>{' '}
            {modelName && <span className="font-normal text-blue-700">({modelName})</span>}
          </p>
          {config.privacy_notice && <p className="mt-1 text-blue-700">{config.privacy_notice}</p>}
        </div>
      )}

      <div className="mb-4">
        <label className="mb-1 block text-sm font-medium text-gray-700">Input source</label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              setInputMode('pmc_id')
              setPmcPreview(null)
              setError(null)
            }}
            className={`rounded-md px-3 py-2 text-sm ${inputMode === 'pmc_id' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            PMC ID
          </button>
          <button
            type="button"
            onClick={() => {
              setInputMode('text')
              setPmcPreview(null)
              setError(null)
            }}
            className={`rounded-md px-3 py-2 text-sm ${inputMode === 'text' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            Manuscript text
          </button>
        </div>
      </div>

      {inputMode === 'pmc_id' ? (
        <div className="mb-4 space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">PMC ID</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={pmcId}
                onChange={(e) => setPmcId(e.target.value)}
                placeholder="e.g. PMC1234567"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
              <button
                type="button"
                onClick={handleFetchPmc}
                disabled={fetchingPmc}
                className="whitespace-nowrap rounded-md bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {fetchingPmc ? 'Fetching…' : 'Fetch full text'}
              </button>
            </div>
          </div>

          {pmcPreview && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
              <p className="mb-1 font-medium text-gray-800 whitespace-pre-line">{pmcPreview.title}</p>
              <p className="max-h-40 overflow-auto text-gray-600 whitespace-pre-line">
                {pmcPreview.text.slice(0, 800)}
                {pmcPreview.text.length > 800 && (
                  <span className="text-gray-400"> … ({pmcPreview.text.length.toLocaleString()} characters total)</span>
                )}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-gray-700">Manuscript text</label>
          <textarea
            value={manuscript}
            onChange={(e) => setManuscript(e.target.value)}
            rows={8}
            placeholder="Paste the manuscript text here..."
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
      )}

      <div className="mb-4">
        <button
          type="button"
          onClick={handleExtract}
          disabled={loading || (inputMode === 'pmc_id' && !pmcPreview)}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
        >
          {loading ? 'Extracting…' : 'Send to LLM'}
        </button>
      </div>

      {job && (job.status === 'pending' || job.status === 'running') && (
        <div className="mb-4 flex items-center gap-3 rounded-md bg-blue-50 p-3 text-sm text-blue-800">
          <span className="h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-blue-300 border-t-blue-600" />
          <span>{job.progress || 'Starting…'}</span>
        </div>
      )}

      {error && <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {message && <div className="mb-4 rounded-md bg-green-50 p-3 text-sm text-green-700">{message}</div>}

      {job && job.status === 'failed' && job.error_message && (
        <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
          Extraction failed: {job.error_message}
        </div>
      )}
    </div>
  )
}
